import { supabase } from '../lib/supabase'
import type { Client, ClientListFilters } from '../types/clients'

function mapClientRow(row: Record<string, unknown>): Client {
  const idScgpRaw = row.id_scgp
  const id_scgp =
    typeof idScgpRaw === 'number'
      ? idScgpRaw
      : idScgpRaw != null && Number.isFinite(Number(idScgpRaw))
        ? Number(idScgpRaw)
        : 0

  return {
    id: String(row.id ?? ''),
    id_scgp,
    name: typeof row.name === 'string' ? row.name : '',
    cnpj: typeof row.cnpj === 'string' ? row.cnpj : '',
    city: typeof row.city === 'string' ? row.city : null,
    state: typeof row.state === 'string' ? row.state : null,
    created_at: typeof row.created_at === 'string' ? row.created_at : null,
    updated_at: typeof row.updated_at === 'string' ? row.updated_at : null,
    deleted_at: typeof row.deleted_at === 'string' ? row.deleted_at : null,
  }
}

const CLIENT_SELECT =
  'id, id_scgp, name, cnpj, city, state, created_at, updated_at, deleted_at'

export const clientsService = {
  async getById(id: string): Promise<Client | null> {
    const trimmed = id.trim()
    if (!trimmed) return null

    const { data, error } = await supabase
      .from('clients')
      .select(CLIENT_SELECT)
      .eq('id', trimmed)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    if (!data) return null
    return mapClientRow(data as Record<string, unknown>)
  },

  async list(filters: ClientListFilters = {}): Promise<Client[]> {
    let query = supabase
      .from('clients')
      .select(CLIENT_SELECT)
      .is('deleted_at', null)
      .order('name', { ascending: true })

    const name = filters.name?.trim()
    if (name) {
      query = query.ilike('name', `%${name}%`)
    }

    const codeDigits = filters.code?.replace(/\D/g, '') ?? ''
    if (codeDigits) {
      const num = parseInt(codeDigits, 10)
      if (!Number.isNaN(num)) {
        query = query.eq('id_scgp', num)
      }
    }

    const cnpjDigits = filters.cnpj?.replace(/\D/g, '') ?? ''
    if (cnpjDigits) {
      query = query.ilike('cnpj', `%${cnpjDigits}%`)
    }

    const state = filters.state?.trim().toUpperCase()
    if (state) {
      query = query.eq('state', state)
    }

    const { data, error } = await query
    if (error) throw error
    return (data ?? []).map((row) => mapClientRow(row as Record<string, unknown>))
  },
}
